/**
 * The earn moment — mockup 4 of the profile/awards document (`.cele`).
 *
 * This file was 950 lines, most of it a particle engine: up to 160
 * absolutely-positioned confetti nodes per open, each animated individually, with a
 * per-category palette so a "profile" badge rained purple. The mockup's position,
 * which I agree with, is that the medallion *is* the celebration and the number is
 * the payload — so the burst is one repeating-conic-gradient behind the medallion
 * plus four static sparks, and there are no JS particles at all. 160 animated nodes
 * stutter inside a Teams webview, the one surface you least want to drop frames on.
 *
 * Escalation for the top two service tiers is deliberate and narrow: a gold field
 * instead of a teal one, a second ray layer at a slight angle, and a slowly
 * breathing halo that respects prefers-reduced-motion. Not more of everything.
 *
 * Copy is one sentence stating what actually happened, replacing a jokey paragraph
 * that varied by category.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ServiceTile, Medallion } from '../awards/AwardBadge';
import { getServiceAwardByTier } from '../awards/serviceAwards';

/** Service tiers, matched to getTenureTier in TenureBadge.jsx. */
const TOTAL_SERVICE_TIERS = 10;

/** The top two tiers get the gold field; everything below is teal. */
function isGoldTier(tier) {
  return tier >= 6;
}

function Rays({ gold }) {
  const mask = 'radial-gradient(closest-side at 50% 100%, #000 12%, rgba(0,0,0,0.55) 46%, transparent 78%)';
  return (
    <>
      <span
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 -bottom-[30px] w-[min(420px,150%)]"
        style={{
          height: gold ? 340 : 300,
          opacity: gold ? 0.9 : 0.7,
          backgroundImage: gold
            ? 'repeating-conic-gradient(from 186deg at 50% 100%, var(--gold-ring) 0deg 1.9deg, transparent 1.9deg 8deg)'
            : 'repeating-conic-gradient(from 186deg at 50% 100%, var(--accent-line) 0deg 2.2deg, transparent 2.2deg 11deg)',
          WebkitMaskImage: mask,
          maskImage: mask,
        }}
      />
      {gold && (
        <span
          aria-hidden
          className="absolute left-1/2 -bottom-[30px] w-[min(460px,160%)] h-[340px] opacity-60"
          style={{
            transform: 'translateX(-50%) rotate(4deg)',
            backgroundImage:
              'repeating-conic-gradient(from 186deg at 50% 100%, var(--gold-deep) 0deg 1deg, transparent 1deg 20deg)',
            WebkitMaskImage: 'radial-gradient(closest-side at 50% 100%, #000 8%, rgba(0,0,0,0.4) 44%, transparent 76%)',
            maskImage: 'radial-gradient(closest-side at 50% 100%, #000 8%, rgba(0,0,0,0.4) 44%, transparent 76%)',
          }}
        />
      )}
    </>
  );
}

/** Four, at fixed positions. Randomised sparks read as noise. */
function Sparks({ gold }) {
  const spots = [
    { style: { left: '16%', top: '26%' }, warm: false },
    { style: { right: '19%', top: '19%' }, warm: true },
    { style: { left: '29%', top: '62%' }, warm: true },
    { style: { right: '27%', top: '57%' }, warm: false },
  ];
  return spots.map((spot, i) => (
    <span
      key={i}
      aria-hidden
      className="absolute w-[5px] h-[5px] rounded-full opacity-50"
      style={{
        ...spot.style,
        background: gold || spot.warm ? 'var(--gold-ring)' : 'var(--accent)',
      }}
    />
  ));
}

/**
 * @param {object} props
 * @param {object} props.badge Tenure badge ({tier, name}) or achievement ({icon, name, description}).
 * @param {'tenure'|'achievement'} [props.badgeType]
 * @param {boolean} [props.showConfetti] Kept for callers that preview without the
 *   burst — it now suppresses rays and sparks rather than a particle system.
 * @param {function} [props.onSeeCelebrating] Optional: opens the congrats inbox.
 */
export function BadgeCelebration({
  isOpen,
  onClose,
  badge,
  employeeName,
  showConfetti = true,
  badgeType = 'tenure',
  onSeeCelebrating,
}) {
  const isService = badgeType !== 'achievement';
  const tier = badge?.tier ?? 0;
  const gold = isService && isGoldTier(tier);
  const service = getServiceAwardByTier(tier);

  useEffect(() => {
    // A service milestone never auto-closes: you earned it by working here for a
    // decade, so it waits for you. Achievements you triggered by clicking something
    // do time out.
    if (!isOpen || isService) return undefined;
    const timer = setTimeout(() => onClose?.(), 8000);
    return () => clearTimeout(timer);
  }, [isOpen, isService, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !badge) return null;

  const title = isService ? service.title : (badge.name || 'Achievement');
  const message = isService
    ? `${service.years === 0 ? 'Your first milestone' : `${service.years} years`} at MOREgroup. `
      + 'Your service award is recorded on your profile, and your team has been notified.'
    : `${badge.description || 'Achievement unlocked.'} It is recorded on your profile.`;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} — ${employeeName || 'you'}`}
      >
        <motion.div
          className={`relative overflow-hidden rounded-[20px] bg-surface border border-line shadow-e3 w-full
                      ${gold ? 'max-w-[428px]' : 'max-w-[392px]'}`}
          initial={{ opacity: 0, scale: 0.94, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className={`relative overflow-hidden text-center ${gold ? 'px-6 pt-[30px] pb-[22px]' : 'px-6 pt-[26px] pb-5'}`}
            style={{
              backgroundImage: gold
                ? 'linear-gradient(180deg, var(--gold-face), transparent)'
                : 'linear-gradient(180deg, var(--accent-soft), transparent)',
            }}
          >
            {showConfetti && <Rays gold={gold} />}
            {showConfetti && <Sparks gold={gold} />}
            {gold && (
              <span
                aria-hidden
                className="absolute left-1/2 top-1/2 w-[172px] h-[172px] -ml-[86px] -mt-[86px] rounded-full badge-halo"
                style={{ border: '1px solid var(--gold-ring)' }}
              />
            )}

            <p
              className="relative text-[10px] font-bold uppercase tracking-[0.14em] mb-3.5"
              style={{ color: gold ? 'var(--gold-ink)' : 'var(--accent)' }}
            >
              {isService ? 'Milestone reached' : 'Achievement earned'}
            </p>

            {/* The earn moment is where the award object is largest — 112px for the
                crest, which is the only place its silhouette is fully readable. */}
            <span className="relative inline-flex justify-center w-full">
              {isService
                ? <ServiceTile years={service.years} size={gold ? 112 : 96} />
                : <Medallion size={96} metal="brand">{badge.icon || '★'}</Medallion>}
            </span>

            <h2 className={`relative font-extrabold tracking-[-0.02em] text-ink mt-[15px] mb-[3px] ${gold ? 'text-[23px]' : 'text-[20px]'}`}>
              {title}
            </h2>
            <p className="relative text-[12.5px] text-ink-3">
              {[employeeName, isService ? badge.name : null].filter(Boolean).join(' · ')}
            </p>
          </div>

          <div className="px-6 pb-[18px] text-center">
            <p className="text-[13px] leading-[1.6] text-ink-2 m-0">{message}</p>
            {isService && (
              <div className="flex justify-center gap-2 flex-wrap mt-3.5">
                <span className="inline-flex items-center h-[24px] px-2.5 rounded-full border border-line bg-surface-2 text-[11.5px] text-ink-2">
                  Tier <span className="font-mono font-semibold mx-1">{tier + 1}</span> of {TOTAL_SERVICE_TIERS}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-2 px-5 py-3.5 border-t border-line bg-surface-2">
            {onSeeCelebrating && (
              <button
                onClick={() => { onSeeCelebrating(); onClose?.(); }}
                className="flex-1 h-9 rounded-ctl text-[12.5px] font-semibold bg-accent text-[var(--on-accent)]"
              >
                See who&apos;s celebrating
              </button>
            )}
            <button
              onClick={onClose}
              autoFocus
              className="flex-1 h-9 rounded-ctl text-[12.5px] font-semibold border border-line-strong bg-surface
                         text-ink-2 hover:bg-surface-hover"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
